import React from "react";
import { X, ChevronRight, Loader2 } from "lucide-react";
import { FileText, Clock, CheckCircle, XCircle } from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", { 
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit"
  });
};

export function VersionsModal({
  selectedQuote,
  versions,
  versionsLoading,
  onClose,
  onNavigateToQuote,
}) {
  if (!selectedQuote) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="versions-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Version History</h3>
            <p className="text-sm text-slate-500">{selectedQuote.clientName || "Untitled Quote"}</p>
          </div>
          <button
            data-testid="close-versions-modal"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {versionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-[#3c68ff]" size={24} />
            </div>
          ) : versions.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No version history available</p>
          ) : (
            <div className="space-y-3">
              {versions.map((version, index) => {
                const statusConfig = STATUS_CONFIG[version.status || "draft"];
                const StatusIcon = statusConfig.icon;
                const isLatest = index === versions.length - 1;
                
                return (
                  <div
                    key={version.id}
                    className={`p-4 rounded-lg border ${isLatest ? "border-[#3c68ff] bg-[#3c68ff]/5" : "border-slate-200"}`}
                    data-testid={`version-item-${version.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isLatest ? "text-[#3c68ff]" : "text-slate-700"}`}>
                          Version {version.version || 1}
                        </span>
                        {isLatest && (
                          <span className="px-2 py-0.5 bg-[#3c68ff] text-white text-xs rounded-full">Latest</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon size={10} />
                          {statusConfig.label}
                        </span>
                      </div>
                      <button
                        data-testid={`open-version-${version.id}`}
                        onClick={() => onNavigateToQuote(version.id)}
                        className="flex items-center gap-1 text-sm text-[#3c68ff] hover:underline"
                      >
                        Open <ChevronRight size={14} />
                      </button>
                    </div>
                    <div className="text-sm text-slate-500">
                      Created: {formatDateTime(version.createdAt)}
                    </div>
                    <div className="text-sm text-slate-500">
                      By: {version.ownerName || version.preparedBy || "Unknown"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
